import React, { useEffect, useState, useCallback } from 'react';
import { Archive, RotateCcw, Trash2 } from 'lucide-react';
import api from '../lib/api';

/**
 * Reusable Trash panel. Drop on any admin list page that supports the
 * standard soft-delete endpoint trio: `/admin/{resource}/trash`,
 * `POST /admin/{resource}/{id}/restore`, `DELETE /admin/{resource}/{id}/purge`.
 *
 * Hides itself when empty AND not open (zero clutter when nothing's in trash).
 * Polls only on demand — no background polling.
 *
 * Props:
 *   resourcePath     – e.g. "/admin/events", "/admin/members", "/admin/packages"
 *   resourceLabel    – singular noun for the confirm dialogs, e.g. "Event"
 *   idField          – key used in the URL when restoring/purging. Defaults
 *                       to `slug`; pass `id` for events.
 *   nameField        – display label per row. Defaults `name`; events/packages
 *                       want `title`.
 *   onChange         – called whenever the list mutates so parent can refresh.
 */
export default function TrashPanel({
  resourcePath,
  resourceLabel,
  idField = 'slug',
  nameField = 'name',
  onChange,
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState({}); // itemId -> bool

  const load = useCallback(() => {
    api.get(`${resourcePath}/trash`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [resourcePath]);

  useEffect(load, [load]);

  async function restore(item) {
    const itemId = item[idField];
    setBusy((b) => ({ ...b, [itemId]: true }));
    try {
      await api.post(`${resourcePath}/${encodeURIComponent(itemId)}/restore`);
      load();
      onChange && onChange();
    } finally {
      setBusy((b) => ({ ...b, [itemId]: false }));
    }
  }

  async function purge(item) {
    const itemId = item[idField];
    const name = item[nameField] || itemId;
    if (!window.confirm(`Permanently delete ${resourceLabel.toLowerCase()} "${name}"? This cannot be undone.`)) return;
    if (!window.confirm('Are you absolutely sure? This is irreversible.')) return;
    setBusy((b) => ({ ...b, [itemId]: true }));
    try {
      await api.delete(`${resourcePath}/${encodeURIComponent(itemId)}/purge`);
      load();
      onChange && onChange();
    } finally {
      setBusy((b) => ({ ...b, [itemId]: false }));
    }
  }

  if (items.length === 0 && !open) return null;

  return (
    <div className="mb-6" data-testid={`trash-panel-${resourcePath.replace(/\//g, '-')}`}>
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="toggle-trash"
        className="w-full text-left bg-stone/60 border border-charcoal/10 rounded-xl px-5 py-3 flex items-center justify-between hover:bg-stone transition-colors"
      >
        <span className="inline-flex items-center gap-2 text-sm font-semibold">
          <Archive size={15} />
          Trash <span className="text-charcoal/50 font-normal">({items.length})</span>
        </span>
        <span className="text-xs text-muted">
          {items.length === 0
            ? `No soft-deleted ${resourceLabel.toLowerCase()}s`
            : open ? 'Click to hide' : 'Click to view and restore'}
        </span>
      </button>
      {open && items.length > 0 && (
        <div className="mt-2 bg-white border border-charcoal/10 rounded-xl divide-y divide-charcoal/5" data-testid="trash-list">
          {items.map((t) => {
            const itemId = t[idField];
            return (
              <div key={itemId} className="px-5 py-3 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t[nameField] || itemId}</div>
                  <div className="text-xs text-muted truncate">
                    <code>{itemId}</code>
                    {t.deleted_at && <span> · deleted {new Date(t.deleted_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span>}
                    {t.deleted_by && <span> by {t.deleted_by}</span>}
                  </div>
                </div>
                <button
                  onClick={() => restore(t)}
                  disabled={busy[itemId]}
                  data-testid={`restore-${itemId}`}
                  className="text-xs px-3 py-1.5 rounded-md border border-forest text-forest hover:bg-forest hover:text-white disabled:opacity-50 inline-flex items-center gap-1 transition-colors"
                >
                  <RotateCcw size={12} /> Restore
                </button>
                <button
                  onClick={() => purge(t)}
                  disabled={busy[itemId]}
                  data-testid={`purge-${itemId}`}
                  className="text-xs px-3 py-1.5 rounded-md border border-red-500/40 text-red-700 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <Trash2 size={12} /> Delete forever
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
